import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLatestVideos,
  getLightingPostVideos,
  getWisdomLampBehindScenes,
  getTransmissionBehindScenes,
  getJointExhibitionVideos,
} from '../api/videoCollectionService';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '@shared/utils/videoUtils';
import { usePageLayout } from '@app/router';
import { ROUTES } from '@app/router/routes';
import { useLanguage } from '@shared/contexts/LanguageContext';
import VideoCategorySection from '../components/VideoCategorySection';

/**
 * 影片集：彙整最新影音／文星點燈傳燈影音／文星點燈花絮／中台心傳花絮，分類顯示並可展開收合
 */
const VideoCollectionPage = () => {
  const { setLayoutOptions } = usePageLayout();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();

  const [latestVideos, setLatestVideos] = useState([]);
  const [lightingPosts, setLightingPosts] = useState([]);
  const [wisdomLampScenes, setWisdomLampScenes] = useState([]);
  const [jointExhibitionVideos, setJointExhibitionVideos] = useState([]);
  const [transmissionScenes, setTransmissionScenes] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    setLayoutOptions({});
    return () => setLayoutOptions({});
  }, [setLayoutOptions]);

  useEffect(() => {
    getLatestVideos()
      .then((res) => {
        const all = [...(res.pinned_items || []), ...(res.items || [])].filter((item) => item.media_url);
        setLatestVideos(all.map((item) => ({
          id: `latest-${item.news_id}`,
          title: item.title,
          videoUrl: item.media_url,
          thumbnailUrl: '',
          date: item.published_at,
        })));
      })
      .catch((err) => console.error('Failed to fetch latest videos:', err));

    getLightingPostVideos()
      .then((res) => {
        const items = res.items || [];
        setLightingPosts(items.map((post) => ({
          id: `lighting-${post.post_id}`,
          title: post.monastery_name,
          videoUrl: post.video?.[0]?.cache_url,
          thumbnailUrl: post.video?.[0]?.thumbnail?.cache_url,
          monasteryId: post.monastery_id,
          date: post.start_at,
        })));
      })
      .catch((err) => console.error('Failed to fetch lighting post videos:', err));

    getWisdomLampBehindScenes()
      .then((res) => {
        const items = res.items || [];
        setWisdomLampScenes(items.map((scene) => ({
          id: `wisdom-scene-${scene.behind_scenes_id}`,
          title: scene.title,
          videoUrl: scene.media_url,
          thumbnailUrl: '',
          monasteryId: scene.monastery_id,
          date: scene.published_at,
        })));
      })
      .catch((err) => console.error('Failed to fetch wisdom lamp behind scenes:', err));

    getTransmissionBehindScenes()
      .then((res) => {
        const items = res.items || [];
        setTransmissionScenes(items.map((scene) => ({
          id: `transmission-scene-${scene.behind_scenes_id}`,
          title: scene.title,
          videoUrl: scene.media_url,
          thumbnailUrl: '',
          monasteryId: scene.monastery_id,
          date: scene.published_at,
        })));
      })
      .catch((err) => console.error('Failed to fetch transmission behind scenes:', err));

    getJointExhibitionVideos()
      .then((res) => {
        const items = res.items || [];
        setJointExhibitionVideos(items.map((scene) => ({
          id: `joint-exhibition-${scene.behind_scenes_id}`,
          title: scene.title,
          videoUrl: scene.media_url,
          thumbnailUrl: '',
          monasteryId: scene.monastery_id,
          date: scene.published_at,
        })));
      })
      .catch((err) => console.error('Failed to fetch joint exhibition videos:', err));
  }, [currentLanguage]);

  const getLatestDate = (items) => items.reduce((latest, item) => {
    const time = item.date ? new Date(item.date).getTime() : 0;
    return time > latest ? time : latest;
  }, 0);

  const jointExhibitionCategory = {
    key: 'jointExhibition',
    titleBlack: t('video-collection.jointExhibitionTitleBlack'),
    titleRed: t('video-collection.jointExhibitionTitleRed'),
    items: jointExhibitionVideos,
  };
  const transmissionScenesCategory = {
    key: 'transmissionScenes',
    titleBlack: t('video-collection.transmissionScenesTitleBlack'),
    titleRed: t('video-collection.transmissionScenesTitleRed'),
    items: transmissionScenes,
  };

  // 聯展影片固定緊接在中台心傳精舍影片正上方，兩者一起依日期排序、不拆散
  const sortedCategories = [
    { key: 'latest', titleBlack: t('video-collection.latestTitleBlack'), titleRed: t('video-collection.latestTitleRed'), items: latestVideos },
    { key: 'lighting', titleBlack: t('video-collection.lightingTitleBlack'), titleRed: t('video-collection.lightingTitleRed'), items: lightingPosts },
    { key: 'wisdomScenes', titleBlack: t('video-collection.wisdomScenesTitleBlack'), titleRed: t('video-collection.wisdomScenesTitleRed'), items: wisdomLampScenes },
  ]
    .filter((cat) => cat.items.length > 0)
    .sort((a, b) => getLatestDate(b.items) - getLatestDate(a.items));

  if (transmissionScenesCategory.items.length > 0 || jointExhibitionCategory.items.length > 0) {
    const pinnedDate = Math.max(
      getLatestDate(transmissionScenesCategory.items),
      getLatestDate(jointExhibitionCategory.items),
    );
    const insertAt = sortedCategories.findIndex((cat) => getLatestDate(cat.items) < pinnedDate);
    const pinnedPair = [jointExhibitionCategory, transmissionScenesCategory].filter((cat) => cat.items.length > 0);
    if (insertAt === -1) {
      sortedCategories.push(...pinnedPair);
    } else {
      sortedCategories.splice(insertAt, 0, ...pinnedPair);
    }
  }

  const categories = sortedCategories;

  return (
    <div style={{ backgroundColor: '#FEFEFE' }}>
      {/* ── Banner ── */}
      <div className="relative w-full">
        <img
          src="/image/video-collection/banner.jpg"
          alt={t('video-collection.pageTitle')}
          className="w-full h-auto block"
        />
      </div>

      {/* ── 頁首 ── */}
      <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
        {/* <section className="px-6 pt-8 md:pt-14 pb-0">
          <h1 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px', borderLeft: '6px solid #BC313D', paddingLeft: 20, color: '#171717' }}>
            {t('video-collection.pageTitle')}
          </h1>
        </section> */}

        {categories.map((cat, idx) => (
          <VideoCategorySection
            key={cat.key}
            titleBlack={cat.titleBlack}
            titleRed={cat.titleRed}
            items={cat.items}
            onSelect={setSelectedVideo}
            viewMoreText={t('video-collection.viewMore')}
            collapseText={t('video-collection.collapse')}
            isFirst={idx === 0}
            isLast={idx === categories.length - 1}
          />
        ))}
      </div>

      {/* ── 影片 Modal ── */}
      {selectedVideo?.videoUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <button
            onClick={() => setSelectedVideo(null)}
            className="fixed top-4 right-4 z-[10000] w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 text-white transition-all"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isYouTubeUrl(selectedVideo.videoUrl) ? (
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedVideo.videoUrl)}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <video
                  src={selectedVideo.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
            {selectedVideo.monasteryId && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => navigate(ROUTES.BRANCH_REPORT_DETAIL(selectedVideo.monasteryId))}
                  className="flex items-center gap-2 px-6 py-2 rounded-full text-white transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#004682', fontFamily: 'Noto Sans TC', fontSize: 15, fontWeight: 500 }}
                >
                  {t('video-collection.goToReport')}
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCollectionPage;
